import { z } from "zod";

import { createTypedDataValueSchema } from "@vm/tableau";

const RawAttributeSchema = z.object({
  name: z.string().nullable(),
  value: z.string().nullable(),
  meta: z
    .object({
      description: z.string().optional().nullable(),
      datatype: z
        .enum(["numeric", "string", "percentage"])
        .default("string")
        .nullable()
        .optional(),
    })
    .optional(),
});

const RawCharacteristicGroupSchema = z.object({
  Correction: z.number(),
  Formatted_name: z.string(),
  Attributes: z.array(RawAttributeSchema),
});

const RawComparisonCharacteristicsSchema = z.record(
  z.string(),
  RawCharacteristicGroupSchema,
);

const parseJsonTypedDataValue = (input: unknown) => {
  if (!input || typeof input !== "object") {
    return input;
  }

  const typedDataValue = { ...input } as {
    value?: unknown;
    nativeValue?: unknown;
  };

  if (typeof typedDataValue.value === "string") {
    try {
      typedDataValue.value = JSON.parse(typedDataValue.value);
    } catch {
      // Leave the original value in place so schema validation fails normally.
    }
  }

  if (typeof typedDataValue.nativeValue === "string") {
    try {
      typedDataValue.nativeValue = JSON.parse(typedDataValue.nativeValue);
    } catch {
      // Leave the original value in place so schema validation fails normally.
    }
  }

  return typedDataValue;
};

export const RawComparisonObjectSchema = z.object({
  name: createTypedDataValueSchema(z.string()),
  type: createTypedDataValueSchema(z.string()),
  value: createTypedDataValueSchema(z.string()),
  characteristics: z
    .preprocess(
      parseJsonTypedDataValue,
      createTypedDataValueSchema(z.array(RawComparisonCharacteristicsSchema)),
    )
    .optional(),
});

export type ComparisonObject = z.infer<typeof RawComparisonObjectSchema>;
export type CharacteristicGroup = z.infer<typeof RawCharacteristicGroupSchema>;
export type ComparisonCharacteristics = z.infer<
  typeof RawComparisonCharacteristicsSchema
>;

export interface ComparisonDataService {
  initialize(): Promise<void>;
  onVizDataLoaded(callback: (objects: ComparisonObject[]) => void): () => void;
}
